import { NextPage } from 'next';

import { PageLayout } from '@klokhuis/core/PageLayout';
import { ROUTES } from '@klokhuis/core/Routing';

import { AgentsKnowledgeHubContent } from './components/AgentsKnowledgeHubContent';

const ORIGIN = 'https://www.findmyprops.com';
const HUB_URL = `${ORIGIN}/agents/knowledge-hub`;

const KNOWLEDGE_HUB_JSON_LD = [
  // CollectionPage — describes the hub itself
  {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    '@id': `${HUB_URL}#page`,
    url: HUB_URL,
    name: 'Agents Knowledge Hub — FindMyProps',
    description:
      'Expert guides, digital marketing strategies, and platform tutorials for real estate agents in India. Free resources to help agents grow their business on FindMyProps.',
    publisher: { '@type': 'Organization', name: 'FindMyProps', url: ORIGIN },
    inLanguage: 'en-IN',
  },
  // ItemList — AI tools use this to understand the article index
  {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'Agents Knowledge Hub Articles',
    url: HUB_URL,
    numberOfItems: 12,
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'How to Use Direct Chat on FindMyProps to Close Deals Faster',
        url: `${HUB_URL}#direct-chat`,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Why Verified Buyers on FindMyProps Mean Higher-Quality Leads',
        url: `${HUB_URL}#verified-buyers`,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: 'React to Property: The Feature That Tells You Exactly What Buyers Want',
        url: `${HUB_URL}#react-to-property`,
      },
      {
        '@type': 'ListItem',
        position: 4,
        name: 'SEO for Real Estate Agents: How to Rank Your Property Listings in India',
        url: `${HUB_URL}#seo-for-agents`,
      },
      {
        '@type': 'ListItem',
        position: 5,
        name: '10 Proven Strategies to Generate More Buyer Leads for Indian Real Estate Agents',
        url: `${HUB_URL}#lead-generation`,
      },
      {
        '@type': 'ListItem',
        position: 6,
        name: 'How to Manage Your Property Listings Effectively on FindMyProps',
        url: `${HUB_URL}#listing-management`,
      },
      {
        '@type': 'ListItem',
        position: 7,
        name: 'Top 5 Emerging Real Estate Markets in South India for 2025',
        url: `${HUB_URL}#emerging-markets`,
      },
      {
        '@type': 'ListItem',
        position: 8,
        name: 'Building Your Personal Brand as a Real Estate Agent in India',
        url: `${HUB_URL}#personal-branding`,
      },
      {
        '@type': 'ListItem',
        position: 9,
        name: 'RERA Compliance Checklist for Real Estate Agents in Tamil Nadu and Andhra Pradesh',
        url: `${HUB_URL}#rera-compliance`,
      },
      {
        '@type': 'ListItem',
        position: 10,
        name: "WhatsApp Business for Real Estate: The Indian Agent's Complete Guide",
        url: `${HUB_URL}#whatsapp-business`,
      },
      {
        '@type': 'ListItem',
        position: 11,
        name: "How FindMyProps's Partner Agent Program Helps You Grow Your Business",
        url: `${HUB_URL}#partner-program`,
      },
      {
        '@type': 'ListItem',
        position: 12,
        name: 'How to Use Data and Analytics to Sell Properties Faster in India',
        url: `${HUB_URL}#data-analytics`,
      },
    ],
  },
  // FAQPage extracted from the hub articles — maximises AI citation surface
  {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'How can real estate agents close deals faster using FindMyProps?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'FindMyProps offers a Direct Chat feature that connects agents instantly with verified buyers — no middlemen, no missed leads. Agents who respond within 15 minutes of an inquiry are significantly more likely to book a site visit. The platform stores the full conversation history so agents can follow up with prospects weeks later.',
        },
      },
      {
        '@type': 'Question',
        name: 'What is the best way to generate buyer leads in India as a real estate agent?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Effective lead generation for Indian real estate agents combines: an optimised FindMyProps agent profile with a verified badge; WhatsApp Business broadcast lists segmented by buyer budget and property type; hyper-local SEO targeting long-tail keywords like "residential plots near [locality]"; YouTube property tour videos; Instagram Reels; Facebook local real estate groups; and Google Ads with budgets as low as ₹3,000/month targeting specific micro-markets.',
        },
      },
      {
        '@type': 'Question',
        name: 'What are the top emerging real estate markets in South India in 2025?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'The top five emerging real estate markets in South India for 2025 are: 1) Hosur–Krishnagiri corridor (Tamil Nadu) driven by EV manufacturing; 2) Coimbatore West — Kinathukadavu to Pollachi, accessible pricing with strong NRI demand; 3) Tiruppur textile township expansion near proposed SEZ zones; 4) Madurai–Dindigul IT corridor with new IT parks; and 5) Krishnapatnam–Nellore industrial zone in Andhra Pradesh, driven by port and PCPIR development.',
        },
      },
      {
        '@type': 'Question',
        name: 'What RERA compliance obligations do real estate agents have in Tamil Nadu?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Real estate agents in Tamil Nadu must register with TNRERA before facilitating transactions involving RERA-registered projects. They must display their TNRERA registration number on all marketing materials, must not represent unregistered projects, and must maintain transaction records for 5 years. Registration is verifiable at rera.tn.gov.in.',
        },
      },
      {
        '@type': 'Question',
        name: 'How do I use WhatsApp Business effectively as a real estate agent in India?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'For Indian real estate agents, WhatsApp Business should be set up with: a professional profile including RERA registration number; a property catalog with photos, prices, and links to FindMyProps listings; quick reply templates for common inquiries; and an auto-reply acknowledging inquiries within 2 hours. Use Broadcast Lists (not groups) segmented by buyer type and budget. Send no more than 2–3 broadcasts per week to avoid being muted.',
        },
      },
    ],
  },
];

const AgentsKnowledgeHub: NextPage = () => {
  return (
    <PageLayout
      pageTitle="Agents Knowledge Hub | FindMyProps – Real Estate Guide for Indian Agents"
      metaDescription="Expert guides, digital marketing tips, and platform tutorials to help real estate agents grow their business on FindMyProps. Reach verified buyers across India."
      route={ROUTES.AgentsKnowledgeHub}
      showPropertyLinksGrid={false}
      jsonLd={KNOWLEDGE_HUB_JSON_LD}
    >
      <PageLayout.MainRaw>
        <AgentsKnowledgeHubContent />
      </PageLayout.MainRaw>
    </PageLayout>
  );
};

export default AgentsKnowledgeHub;
